import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Upload, Trash2, Download, Info } from 'lucide-react';
import { useGSDocumentationRequirements } from '@/hooks/useGSDocumentationRequirements';
import { useProductDocuments } from '@/hooks/useProductDocuments';
import { ProductDocumentUploadDialog } from './ProductDocumentUploadDialog';

interface GSProductDocumentationProps {
  productId: string;
}

interface RequirementRowProps {
  requirement: {
    id: string;
    title: string;
    description: string | null;
    content_expected: string | null;
  };
  section: {
    section_number: number;
    title: string;
    is_optional: boolean;
  };
  productId: string;
}

function RequirementRow({ requirement, section, productId }: RequirementRowProps) {
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const { documents, isLoading, refresh, deleteDocument, downloadDocument } = useProductDocuments(
    productId,
    requirement.id
  );

  const handleDelete = async (documentId: string) => {
    if (confirm('Are you sure you want to delete this document?')) {
      await deleteDocument(documentId);
      refresh();
    }
  };

  const handleDownload = async (document: any) => {
    await downloadDocument(document);
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {section.section_number}.
            </span>
            <span className="font-medium text-sm">{section.title}</span>
            {section.is_optional && (
              <Badge variant="outline" className="text-xs">
                Optional
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell className="font-medium">
          <div className="flex items-center gap-2">
            {requirement.title}
            {(requirement.description || requirement.content_expected) && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-sm">
                    {requirement.description && (
                      <p className="mb-2">{requirement.description}</p>
                    )}
                    {requirement.content_expected && (
                      <p className="text-xs italic">Expected: {requirement.content_expected}</p>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </TableCell>
        <TableCell>
          <Badge variant={documents.length > 0 ? "default" : "secondary"}>
            {documents.length} file{documents.length !== 1 ? 's' : ''}
          </Badge>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowUploadDialog(true)}
              className="flex items-center gap-1"
            >
              <Upload className="h-3 w-3" />
              Upload
            </Button>
            {documents.length > 0 && (
              <div className="flex items-center gap-1">
                {documents.slice(0, 2).map((document) => (
                  <div key={document.id} className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDownload(document)}
                      className="p-1 h-6 w-6"
                      title={`Download ${document.file_name}`}
                    >
                      <Download className="h-3 w-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(document.id)}
                      className="p-1 h-6 w-6"
                      title={`Delete ${document.file_name}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                {documents.length > 2 && (
                  <span className="text-xs text-muted-foreground">+{documents.length - 2} more</span>
                )}
              </div>
            )}
          </div>
        </TableCell>
      </TableRow>
      <ProductDocumentUploadDialog
        open={showUploadDialog}
        onOpenChange={setShowUploadDialog}
        productId={productId}
        requirementId={requirement.id}
        requirementTitle={requirement.title}
        onSuccess={() => {
          refresh();
          setShowUploadDialog(false);
        }}
      />
    </>
  );
}

export function GSProductDocumentation({ productId }: GSProductDocumentationProps) {
  const { sections, isLoading } = useGSDocumentationRequirements('GS37');

  // Flatten all requirements from all sections into a single array
  const allRequirements = sections.flatMap(section => 
    section.requirements.map(requirement => ({
      ...requirement,
      section: {
        section_number: section.section_number,
        title: section.title,
        is_optional: section.is_optional
      }
    }))
  );

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>GS-37 Documentation Requirements</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-12 bg-muted rounded-lg mb-2"></div>
                <div className="h-4 bg-muted rounded w-3/4"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          GS-37 Documentation Requirements
          <Badge variant="secondary">{allRequirements.length} requirements</Badge>
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Complete the required documentation for GS-37 certification. Requirements marked as optional can be submitted if applicable to your product.
        </p>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Section</TableHead>
              <TableHead>Requirement</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {allRequirements.map((requirement) => (
              <RequirementRow
                key={requirement.id}
                requirement={requirement}
                section={requirement.section}
                productId={productId}
              />
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}